import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import {
  UserIcon,
  UsersIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  RefreshButton,
  PageHeader,
  SearchAndFilters,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';

const fmtDate = (iso, locale) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso ?? '—';
  }
};

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const formatPlanName = (plan) =>
  plan
    ? plan.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

const formatLabel = (value) =>
  value
    ? value
      .toString()
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

const Badge = ({ children, color = 'gray' }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-${color}-100 text-${color}-800`}>
    {children}
  </span>
);

const VerifiedBadge = ({ verified, t }) =>
  verified ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      {t('verified')}
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">
      {t('unverified')}
    </span>
  );

const ActiveBadge = ({ active, t }) =>
  active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-blue-500/10 text-blue-400 border border-blue-500/20">
      {t('active')}
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-white/5 text-white/40 border border-white/10">
      {t('inactive')}
    </span>
  );

const PlanBadge = ({ plan }) => (
  <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800">
    {formatPlanName(plan)}
  </span>
);

const SummarySkeleton = () => (
  <div className="grid grid-cols-2 gap-3 sm:gap-6 sm:grid-cols-3">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="controlled-glass p-6 rounded-[32px] flex items-center space-x-4 animate-pulse">
        <div className="h-10 w-10 rounded-xl bg-white/5 flex-shrink-0" />
        <div className="flex-1">
          <div className="h-3 w-20 bg-white/10 rounded mb-2" />
          <div className="h-4 w-12 bg-white/5 rounded" />
        </div>
      </div>
    ))}
  </div>
);

const UserList = () => {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { usersAdmin, adminUsersError, loading, fetchAdminUsers } = useAppStore();

  const [booting, setBooting] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm, 250);

  const [filterVerified, setFilterVerified] = useState('all');
  const [filterActive, setFilterActive] = useState('all');

  useEffect(() => {
    let active = true;
    (async () => {
      await fetchAdminUsers();
      if (active) setBooting(false);
    })();
    return () => {
      active = false;
    };
  }, [fetchAdminUsers]);

  const baseUsers = useMemo(() => {
    const meId = currentUser?.id ?? currentUser?._id ?? currentUser?.uid;
    return (usersAdmin || []).filter((u) => !(u.role === 'superadmin' && u.id !== meId));
  }, [usersAdmin, currentUser]);

  const usersSorted = useMemo(
    () => [...baseUsers].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [baseUsers]
  );

  const filteredUsers = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase();
    return usersSorted.filter((u) => {
      const textOk = !s
        ? true
        : [u.name, u.email, u.company_name, u.role, u.subscription_plan, u.subscription_status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s));
      const verOk =
        filterVerified === 'all' ||
        (filterVerified === 'verified' && Boolean(u.is_verified)) ||
        (filterVerified === 'unverified' && !Boolean(u.is_verified));
      const actOk =
        filterActive === 'all' ||
        (filterActive === 'active' && Boolean(u.is_active)) ||
        (filterActive === 'inactive' && !Boolean(u.is_active));
      return textOk && verOk && actOk;
    });
  }, [usersSorted, debouncedSearch, filterVerified, filterActive]);

  const usersWithAgents = useMemo(
    () => baseUsers.filter((u) => (u.total_agents ?? 0) > 0).length,
    [baseUsers]
  );
  const usersWithoutAgents = Math.max(0, (baseUsers?.length ?? 0) - usersWithAgents);

  const isInitialLoading = booting || loading;
  const isSummaryLoading = isInitialLoading || refreshing;

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      await fetchAdminUsers();
    } finally {
      setRefreshing(false);
    }
  }, [fetchAdminUsers]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title={t('allUsers')}
        subtitle={t('manageUsers')}
      >
        <RefreshButton onClick={handleRefresh} isLoading={isInitialLoading || refreshing} t={t} />
      </PageHeader>

      {isSummaryLoading ? (
        <SummarySkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-6 sm:grid-cols-3">
          <div className="controlled-glass p-6 rounded-[32px] inner-highlight flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-primary-500/10 border border-primary-500/20">
              <UsersIcon className="h-6 w-6 text-primary-400" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('totalUsers')}</p>
              <p className="text-2xl font-bold text-white tracking-tight">{baseUsers.length}</p>
            </div>
          </div>

          <div className="controlled-glass p-6 rounded-[32px] inner-highlight flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <UserIcon className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('withAgents')}</p>
              <p className="text-2xl font-bold text-white tracking-tight">{usersWithAgents}</p>
            </div>
          </div>

          <div className="controlled-glass p-6 rounded-[32px] inner-highlight flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <UserIcon className="h-6 w-6 text-white/40" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('withoutAgents')}</p>
              <p className="text-2xl font-bold text-white-40 tracking-tight text-white/40">{usersWithoutAgents}</p>
            </div>
          </div>
        </div>
      )}

      <div className="block sm:hidden">
        <div className="space-y-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('searchUsers')}
            disabled={isInitialLoading}
            className="input"
          />
          <div className="flex flex-col space-y-3">
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              disabled={isInitialLoading}
              className="input"
            >
              <option value="all">{t('allVerification')}</option>
              <option value="verified">{t('verified')}</option>
              <option value="unverified">{t('unverified')}</option>
            </select>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              disabled={isInitialLoading}
              className="input"
            >
              <option value="all">{t('allActivity')}</option>
              <option value="active">{t('active')}</option>
              <option value="inactive">{t('inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="hidden sm:block">
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder={t('searchUsersPlaceholder')}
          t={t}
          filters={[
            {
              value: filterVerified,
              onChange: (e) => setFilterVerified(e.target.value),
              options: [
                { value: 'all', label: t('allVerification') },
                { value: 'verified', label: t('verified') },
                { value: 'unverified', label: t('unverified') }
              ]
            },
            {
              value: filterActive,
              onChange: (e) => setFilterActive(e.target.value),
              options: [
                { value: 'all', label: t('allActivity') },
                { value: 'active', label: t('active') },
                { value: 'inactive', label: t('inactive') }
              ]
            }
          ]}
          disabled={isInitialLoading}
        />
      </div>

      {adminUsersError && !isInitialLoading && (
        <div className="card p-4 sm:p-6">
          <p className="text-sm text-red-600">{adminUsersError}</p>
        </div>
      )}

      {isInitialLoading ? (
        <div className="space-y-3 sm:space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-3 sm:p-4 animate-pulse">
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-gray-200" />
                  <div>
                    <div className="h-3 w-32 sm:w-40 bg-gray-200 rounded" />
                    <div className="h-3 w-40 sm:w-56 bg-gray-200 rounded mt-2" />
                    <div className="mt-2 flex items-center gap-1 sm:gap-2 flex-wrap">
                      <div className="h-4 w-12 sm:w-16 bg-gray-200 rounded" />
                      <div className="h-4 w-12 sm:w-16 bg-gray-200 rounded" />
                      <div className="h-4 w-16 sm:w-24 bg-gray-200 rounded" />
                      <div className="h-4 w-14 sm:w-20 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 sm:space-x-6">
                  <div className="text-right hidden sm:block">
                    <div className="h-3 w-20 sm:w-24 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-24 sm:w-28 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-12 sm:w-16 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-20 sm:w-24 bg-gray-200 rounded" />
                  </div>
                  <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-gray-200" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !adminUsersError && (
        <>
          {filteredUsers.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {filteredUsers.map((u) => (
                <button
                  key={u.id ?? u.email}
                  className="controlled-glass p-6 w-full flex justify-between items-center hover:border-white/20 hover:shadow-2xl transition-all text-left inner-highlight group rounded-[32px]"
                  onClick={() => navigate(`/agents?fromUser=true&email=${encodeURIComponent(u.email)}`)}
                  title="View user's agents"
                >
                  <div className="flex items-center space-x-6 flex-1 min-w-0">
                    <div className="p-3 rounded-xl bg-primary-500/10 border border-primary-500/20 group-hover:bg-primary-500/20 transition-colors">
                      <UserIcon className="h-6 w-6 text-primary-400 flex-shrink-0" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold text-white tracking-tight truncate">{u.name ?? '—'}</h3>
                      <p className="text-xs font-bold text-white/30 uppercase tracking-widest mt-0.5 truncate">{u.email ?? '—'}</p>
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <VerifiedBadge verified={u.is_verified} t={t} />
                        <ActiveBadge active={u.is_active} t={t} />
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-primary-500/10 text-primary-400 border border-primary-500/20">
                          {formatPlanName(u.subscription_plan)}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-white/5 text-white/40 border border-white/10">
                          {formatLabel(u.role)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6 ml-6">
                    <div className="text-right hidden sm:block bg-white/5 border border-white/5 rounded-2xl p-4 min-w-[180px]">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('company')}</span>
                        <span className="text-xs font-bold text-white/60 truncate max-w-[100px] text-right">{u.company_name ?? '—'}</span>
                      </div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('minutes')}</span>
                        <span className="text-xs font-bold text-white/60">{(u.call_minutes_used ?? 0)}/{(u.call_minutes_limit ?? 0)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('aiAgents')}</span>
                        <span className="text-xs font-bold text-white/60">{u.total_agents ?? 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('joined')}</span>
                        <span className="text-[10px] font-bold text-white/40">{fmtDate(u.created_at, locale)}</span>
                      </div>
                    </div>
                    <div className="text-right text-xs text-white/40 sm:hidden">
                      <p className="font-bold text-white">{u.total_agents ?? 0} {t('aiAgents').toLowerCase()}</p>
                      <p>{(u.call_minutes_used ?? 0)} min</p>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-white/20 group-hover:text-white/60 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-16 px-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto mb-3 sm:mb-6 h-10 sm:h-14 w-10 sm:w-14 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2a9 9 0 00-9 9v2a9 9 0 009 9 9 9 0 009-9v-2a9 9 0 00-9-9z"
                />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 15c1.333 1 2.667 1 4 0" />
              </svg>
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-white">{t('noUsersYetTitle')}</h3>
              <p className="mt-1 text-xs sm:text-sm text-white/40 max-w-md mx-auto">
                {t('noUsersYetDesc')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserList;